import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { khoOcrRepository, type LocKhoOcr } from '../../../lib/repositories/ocr-file.repository';
import { thuLai } from '../../vai-tro/hooks/useVaiTro';
import type { DocTranscript, TepXemTruoc } from '../../xu-ly/types';
import type { OcrFile, OcrFilePage, OcrUploadResult } from '../types';

export const KHOA_KHO_OCR = {
  goc: ['ocr', 'ocr-files'] as const,
  danhSach: (loc: Omit<LocKhoOcr, 'cursor' | 'limit'>) => ['ocr', 'ocr-files', 'danh-sach', loc] as const,
  chiTiet: (id: string) => ['ocr', 'ocr-files', 'chi-tiet', id] as const,
  banChep: (id: string, versionId: string) => ['ocr', 'ocr-files', 'ban-chep', id, versionId] as const,
  tep: (id: string) => ['ocr', 'ocr-files', 'tep', id] as const,
};

export const MOT_TRANG_KHO = 50;
const NHIP_DANG_DOC = 4_000;

/** Danh sách lọc ở server (`state`, `search`), phân trang con trỏ; còn tài liệu đang đọc thì tự hỏi lại. */
export function useDanhSachKhoOcr(loc: Omit<LocKhoOcr, 'cursor' | 'limit'>) {
  return useInfiniteQuery<OcrFilePage, ApiError>({
    queryKey: KHOA_KHO_OCR.danhSach(loc),
    queryFn: ({ pageParam }) => khoOcrRepository.danhSach({ ...loc, limit: MOT_TRANG_KHO, cursor: pageParam as string | undefined }),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.next_cursor ?? undefined,
    staleTime: 15_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.items.some((f) => f.state === 'RUNNING')) ? NHIP_DANG_DOC : false),
  });
}

export function useChiTietKhoOcr(id: string | undefined) {
  return useQuery<OcrFile, ApiError>({
    queryKey: KHOA_KHO_OCR.chiTiet(id ?? ''),
    queryFn: () => khoOcrRepository.chiTiet(id!),
    enabled: Boolean(id),
    staleTime: 10_000,
    retry: thuLai,
    refetchInterval: (q) => (q.state.data?.state === 'RUNNING' ? NHIP_DANG_DOC : false),
  });
}

export function useBanChepKhoOcr(id: string | undefined, versionId: string | undefined) {
  return useQuery<DocTranscript, ApiError>({
    queryKey: KHOA_KHO_OCR.banChep(id ?? '', versionId ?? ''),
    queryFn: () => khoOcrRepository.banChep(id!, versionId!),
    enabled: Boolean(id && versionId),
    staleTime: 60_000,
    retry: thuLai,
  });
}

export function useTepKhoOcr(id: string | undefined, bat: boolean) {
  return useQuery<TepXemTruoc, ApiError>({
    queryKey: KHOA_KHO_OCR.tep(id ?? ''),
    queryFn: () => khoOcrRepository.tep(id!),
    enabled: Boolean(id) && bat,
    staleTime: 0,
    gcTime: 60_000,
    retry: thuLai,
  });
}

/** Thao tác ghi trên kho bản OCR. Không tự bật toast: phía gọi quyết câu báo và chỗ hiện lỗi. */
export function useGhiKhoOcr() {
  const queryClient = useQueryClient();
  const lamMoi = () => void queryClient.invalidateQueries({ queryKey: KHOA_KHO_OCR.goc });
  const ghi = (f: OcrFile) => {
    queryClient.setQueryData(KHOA_KHO_OCR.chiTiet(f.id), f);
    lamMoi();
  };
  return {
    taiLen: useMutation<OcrUploadResult, ApiError, { files: File[]; reader_key: string; skip_if_done: boolean }>({
      mutationFn: ({ files, reader_key, skip_if_done }) => khoOcrRepository.taiLen(files, reader_key, skip_if_done),
      onSuccess: lamMoi,
    }),
    docLai: useMutation<OcrFile, ApiError, { id: string; reader_key: string }>({
      mutationFn: ({ id, reader_key }) => khoOcrRepository.docLai(id, reader_key),
      onSuccess: ghi,
    }),
    ghim: useMutation<OcrFile, ApiError, { id: string; versionId: string }>({
      mutationFn: ({ id, versionId }) => khoOcrRepository.ghim(id, versionId),
      onSuccess: ghi,
    }),
    boGhim: useMutation<OcrFile, ApiError, { id: string; versionId: string }>({
      mutationFn: ({ id, versionId }) => khoOcrRepository.boGhim(id, versionId),
      onSuccess: ghi,
    }),
    huyDoc: useMutation<OcrFile, ApiError, { id: string; versionId: string }>({
      mutationFn: ({ id, versionId }) => khoOcrRepository.huyDoc(id, versionId),
      onSuccess: ghi,
    }),
    xoaBan: useMutation<void, ApiError, { id: string; versionId: string }>({
      mutationFn: ({ id, versionId }) => khoOcrRepository.xoaBan(id, versionId),
      onSuccess: lamMoi,
    }),
    xoaTep: useMutation<void, ApiError, string>({
      mutationFn: (id) => khoOcrRepository.xoaTep(id),
      onSuccess: (_kq, id) => {
        queryClient.removeQueries({ queryKey: KHOA_KHO_OCR.chiTiet(id) });
        lamMoi();
      },
    }),
  };
}
